
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export interface GeoPromotionLocation {
  id?: string;
  promotion_id?: string;
  latitude: number;
  longitude: number;
  radius_meters: number;
  label: string;
}

export interface GeoPromotion {
  id: string;
  provider_id: string | null;
  business_name: string;
  title: string;
  description: string | null;
  promo_type: string;
  discount_value: number | null;
  discount_unit: string | null;
  latitude: number;
  longitude: number;
  radius_meters: number;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
  max_notifications: number | null;
  current_notifications: number;
  target_species: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
  locations: GeoPromotionLocation[];
}

export interface GeoPromotionFormData {
  business_name: string;
  title: string;
  description?: string;
  promo_type: string;
  discount_value?: number;
  discount_unit?: string;
  latitude: number;
  longitude: number;
  radius_meters: number;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
  max_notifications?: number;
  target_species: string[];
  provider_id?: string;
  locations: GeoPromotionLocation[];
}

export const useAdminGeoPromotions = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: promotions = [], isLoading } = useQuery({
    queryKey: ['admin-geo-promotions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('geo_promotions')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;

      // Fetch locations for all promotions
      const promoIds = (data || []).map((p: any) => p.id);
      let locations: any[] = [];
      if (promoIds.length > 0) {
        const { data: locData, error: locError } = await supabase
          .from('geo_promotion_locations')
          .select('*')
          .in('promotion_id', promoIds);
        if (!locError) locations = locData || [];
      }

      const locationsByPromo: Record<string, GeoPromotionLocation[]> = {};
      locations.forEach((loc: any) => {
        if (!locationsByPromo[loc.promotion_id]) locationsByPromo[loc.promotion_id] = [];
        locationsByPromo[loc.promotion_id].push({
          id: loc.id,
          promotion_id: loc.promotion_id,
          latitude: Number(loc.latitude),
          longitude: Number(loc.longitude),
          radius_meters: loc.radius_meters,
          label: loc.label || '',
        });
      });

      return (data || []).map((p: any) => ({
        ...p,
        locations: locationsByPromo[p.id] || [],
      })) as unknown as GeoPromotion[];
    },
  });

  const { data: impressionStats = {} } = useQuery({
    queryKey: ['admin-geo-promotion-impressions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('geo_promotion_impressions')
        .select('promotion_id, clicked');
      if (error) throw error;
      const stats: Record<string, { sent: number; clicked: number }> = {};
      (data || []).forEach((imp: any) => {
        if (!stats[imp.promotion_id]) stats[imp.promotion_id] = { sent: 0, clicked: 0 };
        stats[imp.promotion_id].sent++;
        if (imp.clicked) stats[imp.promotion_id].clicked++;
      });
      return stats;
    },
  });

  const saveLocations = async (promotionId: string, locations: GeoPromotionLocation[]) => {
    // Delete existing locations for this promotion
    await supabase
      .from('geo_promotion_locations')
      .delete()
      .eq('promotion_id', promotionId);

    // Insert new locations
    if (locations.length > 0) {
      const rows = locations.map(loc => ({
        promotion_id: promotionId,
        latitude: loc.latitude,
        longitude: loc.longitude,
        radius_meters: loc.radius_meters,
        label: loc.label || null,
      }));
      const { error } = await supabase
        .from('geo_promotion_locations')
        .insert(rows as any);
      if (error) throw error;
    }
  };

  const createMutation = useMutation({
    mutationFn: async (formData: GeoPromotionFormData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { locations, ...promoData } = formData;
      // Use first location as the main lat/lng for backward compatibility
      const mainLocation = locations[0] || { latitude: promoData.latitude, longitude: promoData.longitude, radius_meters: promoData.radius_meters };

      const { data, error } = await supabase.from('geo_promotions').insert({
        ...promoData,
        latitude: mainLocation.latitude,
        longitude: mainLocation.longitude,
        radius_meters: mainLocation.radius_meters,
        created_by: user.id,
        description: promoData.description || null,
        discount_value: promoData.discount_value || null,
        discount_unit: promoData.discount_unit || null,
        max_notifications: promoData.max_notifications || null,
        provider_id: promoData.provider_id || null,
      } as any).select('id').single();
      if (error) throw error;

      // Save all locations
      await saveLocations(data.id, locations);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-geo-promotions'] });
      toast.success(t('geoPromotions.created'));
    },
    onError: (error: any) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...formData }: GeoPromotionFormData & { id: string }) => {
      const { locations, ...promoData } = formData;
      const mainLocation = locations[0] || { latitude: promoData.latitude, longitude: promoData.longitude, radius_meters: promoData.radius_meters };

      const { error } = await supabase
        .from('geo_promotions')
        .update({
          ...promoData,
          latitude: mainLocation.latitude,
          longitude: mainLocation.longitude,
          radius_meters: mainLocation.radius_meters,
          description: promoData.description || null,
          discount_value: promoData.discount_value || null,
          discount_unit: promoData.discount_unit || null,
          max_notifications: promoData.max_notifications || null,
          provider_id: promoData.provider_id || null,
        } as any)
        .eq('id', id);
      if (error) throw error;

      // Save all locations
      await saveLocations(id, locations);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-geo-promotions'] });
      toast.success(t('geoPromotions.updated'));
    },
    onError: (error: any) => toast.error(error.message),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('geo_promotions')
        .update({ is_active } as any)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-geo-promotions'] });
      toast.success(t('geoPromotions.statusUpdated'));
    },
    onError: (error: any) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('geo_promotions').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-geo-promotions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-geo-promotion-impressions'] });
      toast.success(t('geoPromotions.deleted'));
    },
    onError: (error: any) => toast.error(error.message),
  });

  return {
    promotions,
    impressionStats,
    isLoading,
    createPromotion: createMutation.mutate,
    updatePromotion: updateMutation.mutate,
    toggleActive: toggleActiveMutation.mutate,
    deletePromotion: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
